import React, { useCallback, useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import { isMoveInCostsNoteLengthValid, validateOtherPageInputs } from '../validation';
import errorMessages from '@common/constants/errorMessages';
import { MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR } from '@rental-manager/constants/NotificationConstants';
import {
  AdornedInput,
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';
import Modal from '@common/components/ConstellationV10Modal';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  editMode: boolean;
  fromDashboard: boolean;
  otherSettings: Array<{ otherPaymentAmount: number; otherFeeType: string; otherNotes: string }>;
  otherTotalPaymentAmount: number;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  otherMicFromLease: Array<Record<string, any>>;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const OtherPage: React.FC<Props> = ({
  saveStateToReduxStore,
  editMode = false,
  fromDashboard = false,
  otherSettings: propsOtherSettings,
  otherTotalPaymentAmount: propsOtherTotalPaymentAmount,
  moveInCostsPaymentLimits,
  totalPaymentAmount: propsTotalPaymentAmount,
  showModalNotification,
  individualLimit,
  otherMicFromLease,
  pageVisited,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}) => {
  const getEmptyError = () => ({
    otherFeeType: '',
    otherPaymentAmount: '',
    otherNotes: '',
  });

  const otherSettingsCentsToDollars = propsOtherSettings.map((setting) => ({
    ...setting,
    otherPaymentAmount: centsToDollarsDecimal(setting.otherPaymentAmount),
  }));

  const initialIsOpen =
    !editMode && !pageVisited?.has(3) && propsOtherSettings.length === 0 && otherMicFromLease?.length > 0
      ? true
      : Boolean(propsOtherSettings.length);

  const initialOtherSettings = [...otherSettingsCentsToDollars];
  const initialErrorsList = [...propsOtherSettings.map(() => getEmptyError())];

  // Add lease data if conditions are met
  if (!editMode && !pageVisited?.has(3) && propsOtherSettings.length === 0 && otherMicFromLease?.length > 0) {
    otherMicFromLease.forEach((otherMic) => {
      initialOtherSettings.push({
        otherFeeType: otherMic.type,
        otherPaymentAmount: centsToDollarsDecimal(otherMic.value),
        otherNotes: '',
      });
      initialErrorsList.push(getEmptyError());
    });
  }

  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [otherSettings, setOtherSettings] = useState(initialOtherSettings);
  const [errorsList, setErrorsList] = useState(initialErrorsList);

  const addAnotherMoveInCost = useCallback((event = { preventDefault: () => {} }): void => {
    event.preventDefault();
    setOtherSettings((prev) => [...prev, { otherFeeType: '', otherPaymentAmount: '', otherNotes: '' }]);
    setErrorsList((prev) => [...prev, getEmptyError()]);
  }, []);

  const toggleOpen = useCallback((): void => {
    setIsOpen(true);
    if (otherSettings.length === 0) {
      addAnotherMoveInCost();
    }
  }, [otherSettings.length, addAnotherMoveInCost]);

  const skipOtherPage = useCallback(async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipOtherMICEvent({ currentProperty, user });

    const payload = {
      otherSettings: [],
    };

    await saveStateToReduxStore(payload);
  }, [currentProperty, user, saveStateToReduxStore]);

  const handleNotesChange = useCallback((e): void => {
    const key = e.target.dataset.statekey;
    const id = e.target.dataset.id;
    let value = e.target.value;
    let error = '';

    if (!isMoveInCostsNoteLengthValid(value)) {
      value = value.substring(0, MOVE_IN_COSTS_NOTE_MAX_LENGTH);
      error = errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
    }

    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings[id][key] = value;
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors[id][key] = error;
      return newErrors;
    });
  }, []);

  const handleAmountChange = useCallback((e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const id = e.target.dataset.id;
    const value = e.target.value;
    const error = '';
    const otherPaymentAmount = removeNonDigit(value);

    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings[id][key] = otherPaymentAmount;
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors[id][key] = error;
      return newErrors;
    });
  }, []);

  const deleteThisMoveInCost = useCallback((idx): void => {
    setOtherSettings((prev) => {
      const newSettings = [...prev];
      newSettings.splice(idx, 1);
      return newSettings;
    });

    setErrorsList((prev) => {
      const newErrors = [...prev];
      newErrors.splice(idx, 1);
      return newErrors;
    });
  }, []);

  const handleSaveStateToReduxStore = async (e): Promise<any> => {
    e.preventDefault();

    const validInputs = validateOtherPageInputs({
      otherSettings,
      errorsList,
      individualLimit,
      handleErrors: (errors) => {
        if (errors.errorsList) setErrorsList([...errors.errorsList]);
      },
    });

    if (!validInputs) {
      return;
    }

    let currentOtherTotal = 0;
    otherSettings.forEach((setting) => {
      currentOtherTotal += parseInt(dollarsToCents(String(setting.otherPaymentAmount)), 10);
    });

    const totalPaymentAmount =
      propsOtherSettings.length === 0
        ? propsTotalPaymentAmount
        : propsTotalPaymentAmount - propsOtherTotalPaymentAmount;

    if (totalPaymentAmount + currentOtherTotal >= moveInCostsPaymentLimits) {
      await showModalNotification(MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR);
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromOtherMICEvent({
      currentProperty,
      user,
    });

    const payload = {
      otherSettings: otherSettings.map((setting) => ({
        ...setting,
        otherPaymentAmount: parseInt(dollarsToCents(String(setting.otherPaymentAmount)), 10),
      })),
    };

    await saveStateToReduxStore(payload);
  };

  const renderOpenedSection = (): JSX.Element => {
    return (
      <>
        {otherSettings.map((val, idx) => {
          const forHelp = `${otherSettings[idx].otherFeeType.length}/
                                ${MOVE_IN_COSTS_NOTE_MAX_LENGTH}`;
          const notesHelp = `${otherSettings[idx].otherNotes.length}/
                                ${MOVE_IN_COSTS_NOTE_MAX_LENGTH}`;
          return (
            <Box key={idx}>
              <Card
                outlined
                tone="soft"
                css={{
                  boxShadow: 'none',
                  padding: 'default',
                  display: 'grid',
                  gridTemplateRows: 'auto',
                  gap: 'default',
                }}
              >
                <Anchor
                  css={{ textDecoration: 'none', display: 'flex', justifyContent: 'flex-end' }}
                  onClick={() => deleteThisMoveInCost(idx)}
                >
                  Delete
                </Anchor>

                <FormField
                  control={
                    <Input
                      data-statekey="otherFeeType"
                      data-id={idx}
                      className="fs-exclude"
                      placeholder="Elevator Fee"
                      value={otherSettings[idx].otherFeeType}
                      onChange={handleNotesChange}
                      onBlur={handleNotesChange}
                      error={Boolean(errorsList[idx].otherFeeType)}
                    />
                  }
                  description={
                    errorsList[idx].otherFeeType ? (
                      <FormHelp error>{errorsList[idx].otherFeeType}</FormHelp>
                    ) : (
                      <FormHelp>{forHelp}</FormHelp>
                    )
                  }
                  label={<Label>For</Label>}
                />

                <FormField
                  control={
                    <AdornedInput
                      fluid
                      error={Boolean(errorsList[idx].otherPaymentAmount)}
                      input={
                        <AdornedInput.Input
                          min="0"
                          type="number"
                          placeholder="Enter amount"
                          className="fs-exclude input-caret-hide"
                          value={!otherSettings[idx].otherPaymentAmount ? '' : otherSettings[idx].otherPaymentAmount}
                          onChange={handleAmountChange}
                          onBlur={handleAmountChange}
                          onFocus={handleAmountChange}
                          data-statekey="otherPaymentAmount"
                          data-id={idx}
                          error={Boolean(errorsList[idx].otherPaymentAmount)}
                        />
                      }
                      size="md"
                      startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
                    />
                  }
                  description={
                    errorsList[idx].otherPaymentAmount && (
                      <FormHelp error>{errorsList[idx].otherPaymentAmount}</FormHelp>
                    )
                  }
                  label={<Label>Amount</Label>}
                />

                <FormField
                  control={
                    <Input
                      data-statekey="otherNotes"
                      data-id={idx}
                      className="fs-exclude"
                      placeholder="Enter any additional info"
                      value={otherSettings[idx].otherNotes}
                      onChange={handleNotesChange}
                      onBlur={handleNotesChange}
                    />
                  }
                  description={
                    errorsList[idx].otherNotes ? (
                      <FormHelp error>{errorsList[idx].otherNotes}</FormHelp>
                    ) : (
                      <FormHelp>{notesHelp}</FormHelp>
                    )
                  }
                  label={<Label>Note for tenant (optional)</Label>}
                />
              </Card>
            </Box>
          );
        })}
      </>
    );
  };

  const renderMainSection = (): JSX.Element => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <Paragraph textStyle="body-lg-bold">Would you like to collect any other move-in costs?</Paragraph>
        <ToggleButtonGroup aria-label="other MIC options" conjoined value={isOpen ? 'Yes' : ''} size="md">
          <React.Fragment key=".0">
            <ToggleButton css={{ width: '100%' }} value="Yes" onClick={toggleOpen}>
              Yes
            </ToggleButton>
            <ToggleButton css={{ width: '100%' }} value="No" onClick={skipOtherPage}>
              No
            </ToggleButton>
          </React.Fragment>
        </ToggleButtonGroup>
        {isOpen && renderOpenedSection()}
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={fromDashboard ? 'Move-in costs' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderMainSection()}
          <FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.OTHER_PAGE} />
        </Box>
      }
      footer={
        isOpen && (
          <ButtonGroup aria-label="modal actions">
            <TextButton onClick={togglePrevPage}>Back</TextButton>
            <Button emphasis="outlined" onClick={addAnotherMoveInCost}>
              Add another move-in cost
            </Button>
            <Button emphasis="filled" onClick={handleSaveStateToReduxStore}>
              Continue
            </Button>
          </ButtonGroup>
        )
      }
    />
  );
};

export default OtherPage;
